import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Combobox from "../Combobox";
import Icon from "../Icon/Icon";
import { HStack } from "../Layout";
import { Button } from "../ui/button";

// 代表して BusinessPlaceTypeSelect のレスポンス型を参照。
export type PlaceOption = ApiResponseBody<
  "/v2/components/place-select",
  "get"
>["items"][number];

type Props = {
  value?: string;
  /** 初期表示用のラベル。可能であれば指定したほうが表示がスムーズです。 */
  displayValue?: string;
  onChange: (option?: PlaceOption) => void;
  allowEmpty?: boolean;
  hideSearch?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  className?: string;
  /** 指定しない場合自律制御 */
  open?: boolean;
  setOpen?: (open: boolean) => void;
};

/**
 * Place のセレクト
 */
export function PlaceSelect(props: Props) {
  const tc = useCommonTranslation();

  const [_open, _setOpen] = useState(false);
  const open = props.open ?? _open;
  const setOpen = props.setOpen ?? _setOpen;

  const [searchText, setSearchText] = useState("");

  const valueToLabelCache = useValueToLabelCache();

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["place-select", searchText],
    queryFn: async ({ queryKey: [_, searchText] }) =>
      await apiClient.GET("/v2/components/place-select", {
        params: { query: { pageSize: 20, freeText: searchText } },
      }),
    select: (data) =>
      data.data?.items.map((item) => ({
        value: item.id,
        label: item.name,
        data: item,
      })),
  });

  const isLoading = data == null;

  const label = useMemo(() => {
    const itemLabel = data?.find(
      (option) => option.data.id === props.value,
    )?.label;
    const cache = valueToLabelCache.get(props.value ?? "");
    return itemLabel || cache || props.displayValue;
  }, [data, props.displayValue, props.value, valueToLabelCache]);

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
        if (!open) setSearchText("");
      }}
    >
      <HStack>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "justify-between overflow-hidden text-ellipsis",
              props.width && `w-[${props.width}px]`,
              !label && "text-muted-foreground",
              props.className,
            )}
            disabled={props.disabled}
          >
            {label || (isLoading ? "" : tc("pleaseSelect"))}
            <Icon
              iconName="ChevronDownIcon"
              className="ml-2 shrink-0 opacity-50"
            />
          </Button>
        </PopoverTrigger>
        {props.allowEmpty && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => props.onChange(undefined)}
          >
            <Icon iconName="XIcon" />
          </Button>
        )}
      </HStack>
      <PopoverContent
        className={cn(
          "w-full overflow-hidden p-0",
          props.width && `w-[${props.width}px]`,
        )}
        align="start"
      >
        <Combobox
          type="single"
          value={props.value ?? null}
          selectionDisplayStyle="hidden"
          onValueChange={(value) => {
            const option = data?.find((option) => option.data.id === value);
            if (option == null) {
              return; // 値クリアの挙動はここでは行わない
            }
            props.onChange(option.data);
            setSearchText("");
            setOpen(false);
          }}
          isPending={isPending}
          isFetching={isFetching}
          options={data ?? []}
          onSearchChange={setSearchText}
          valueToLabelCache={valueToLabelCache}
          hideSearch={props.hideSearch}
        />
      </PopoverContent>
    </Popover>
  );
}
